'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Sede {
  id: string;
  nome: string;
  tipo: string;
  indirizzo: string;
  citta: string;
  telefono: string;
  responsabile: string;
  note: string;
  is_active: boolean;
}

interface Reparto {
  id: string;
  nome: string;
  codice: string;
  sede_id: string | null;
  piano: number;
  responsabile_reparto: string;
  referente_tecnico: string;
  email_reparto: string;
  telefono_reparto: string;
  note: string;
  is_active: boolean;
  sede?: { nome: string } | null;
}

const TIPO_SEDE_LABELS: Record<string, string> = {
  laboratorio_principale: 'Laboratorio Principale',
  punto_prelievo: 'Punto Prelievo',
  ambulatorio_esterno: 'Ambulatorio Esterno',
  spoke: 'Spoke',
};

const REPARTI_PREDEFINITI = [
  'Accettazione', 'Laboratorio Analisi', 'Ematologia', 'Biochimica',
  'Microbiologia', 'Coagulazione', 'Radiologia', 'Anatomia Patologica',
  'Pronto Soccorso', 'Terapia Intensiva', 'Amministrazione', 'CED', 'Direzione'
];

const emptyReparto: Omit<Reparto, 'id' | 'sede'> = {
  nome: '', codice: '', sede_id: null, piano: 0,
  responsabile_reparto: '', referente_tecnico: '',
  email_reparto: '', telefono_reparto: '', note: '', is_active: true,
};

const emptySede: Omit<Sede, 'id'> = {
  nome: '', tipo: 'laboratorio_principale', indirizzo: '', citta: '',
  telefono: '', responsabile: '', note: '', is_active: true,
};

export default function RepartiContent() {
  const [activeTab, setActiveTab] = useState<'reparti' | 'sedi'>('reparti');
  const [reparti, setReparti] = useState<Reparto[]>([]);
  const [sedi, setSedi] = useState<Sede[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const { canWrite } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        supabase.from('reparti').select('*, sede:sedi(nome)').order('nome'),
        supabase.from('sedi').select('*').order('nome'),
      ]);
      if (rRes.error) throw rRes.error;
      if (sRes.error) throw sRes.error;
      setReparti(rRes.data || []);
      setSedi(sRes.data || []);
    } catch (err: any) {
      toast.error('Errore caricamento: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setFormData(activeTab === 'reparti' ? { ...emptyReparto } : { ...emptySede });
    setShowForm(true);
  };

  const openEdit = (item: any) => {
    setEditItem(item);
    setFormData({ ...item });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const table = activeTab === 'reparti' ? 'reparti' : 'sedi';
      const { sede, ...cleanData } = formData;
      if (editItem) {
        const { error } = await supabase.from(table).update({ ...cleanData, updated_at: new Date().toISOString() }).eq('id', editItem.id);
        if (error) throw error;
        toast.success(activeTab === 'reparti' ? 'Reparto aggiornato' : 'Sede aggiornata');
      } else {
        const { error } = await supabase.from(table).insert(cleanData);
        if (error) throw error;
        toast.success(activeTab === 'reparti' ? 'Reparto creato' : 'Sede creata');
      }
      setShowForm(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore salvataggio: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const table = activeTab === 'reparti' ? 'reparti' : 'sedi';
    try {
      const { error } = await supabase.from(table).delete().eq('id', deleteConfirm.id);
      if (error) throw error;
      toast.success('Eliminato con successo');
      setDeleteConfirm({ open: false, id: '', name: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore eliminazione: ' + err.message);
    }
  };

  const items = activeTab === 'reparti' ? reparti : sedi;

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster richColors position="top-right" />
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface">
        <div>
          <h1 className="text-2xl font-bold text-white">Reparti e Sedi</h1>
          <p className="text-sm text-muted-color mt-0.5">Gestione reparti ospedalieri e sedi operative</p>
        </div>
        {canWrite() && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-semibold transition-colors">
            <Icon name="PlusIcon" size={18} />
            {activeTab === 'reparti' ? 'Nuovo Reparto' : 'Nuova Sede'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-4">
        {(['reparti', 'sedi'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors ${activeTab === tab ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-white hover:bg-surface-elevated'}`}>
            {tab === 'reparti' ? `Reparti (${reparti.length})` : `Sedi (${sedi.length})`}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-muted-color">
            <Icon name="BuildingOfficeIcon" size={48} className="mb-3 opacity-30" />
            <p className="text-lg font-semibold">Nessun elemento trovato</p>
            <p className="text-sm mt-1">Crea il primo {activeTab === 'reparti' ? 'reparto' : 'sede'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((item: any) => (
              <div key={item.id} className="bg-surface border border-border rounded-xl p-5 hover:border-sky-500/40 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center">
                      <Icon name={activeTab === 'reparti' ? 'BuildingOffice2Icon' : 'MapPinIcon'} size={20} className="text-sky-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-base">{item.nome}</h3>
                      {activeTab === 'reparti' && (
                        <span className="text-xs font-mono bg-surface-elevated px-2 py-0.5 rounded text-sky-400">{item.codice}</span>
                      )}
                      {activeTab === 'sedi' && (
                        <span className="text-xs text-muted-color">{TIPO_SEDE_LABELS[item.tipo] || item.tipo}</span>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${item.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                    {item.is_active ? 'Attivo' : 'Inattivo'}
                  </span>
                </div>
                <div className="space-y-1.5 text-sm text-secondary-color">
                  {activeTab === 'reparti' && (
                    <>
                      {item.sede?.nome && <p><span className="text-muted-color">Sede:</span> {item.sede.nome}</p>}
                      <p><span className="text-muted-color">Piano:</span> {item.piano === 0 ? 'Piano Terra' : `Piano ${item.piano}`}</p>
                      {item.responsabile_reparto && <p><span className="text-muted-color">Responsabile:</span> {item.responsabile_reparto}</p>}
                      {item.referente_tecnico && <p><span className="text-muted-color">Ref. Tecnico:</span> {item.referente_tecnico}</p>}
                    </>
                  )}
                  {activeTab === 'sedi' && (
                    <>
                      {item.indirizzo && <p><span className="text-muted-color">Indirizzo:</span> {item.indirizzo}, {item.citta}</p>}
                      {item.responsabile && <p><span className="text-muted-color">Responsabile:</span> {item.responsabile}</p>}
                      {item.telefono && <p><span className="text-muted-color">Tel:</span> {item.telefono}</p>}
                    </>
                  )}
                </div>
                {canWrite() && (
                  <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                    <button onClick={() => openEdit(item)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors">
                      <Icon name="PencilIcon" size={14} /> Modifica
                    </button>
                    <button onClick={() => setDeleteConfirm({ open: true, id: item.id, name: item.nome })} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                      <Icon name="TrashIcon" size={14} /> Elimina
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-bold text-white">
                {editItem ? 'Modifica' : 'Nuovo'} {activeTab === 'reparti' ? 'Reparto' : 'Sede'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color">
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {activeTab === 'reparti' ? (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Nome Reparto *</label>
                    <input list="reparti-list" value={formData.nome || ''} onChange={e => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                      placeholder="es. Laboratorio Analisi" />
                    <datalist id="reparti-list">
                      {REPARTI_PREDEFINITI.map(r => <option key={r} value={r} />)}
                    </datalist>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Codice *</label>
                      <input value={formData.codice || ''} onChange={e => setFormData({ ...formData, codice: e.target.value.toUpperCase() })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="es. LAB" maxLength={10} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Piano</label>
                      <input type="number" value={formData.piano ?? 0} onChange={e => setFormData({ ...formData, piano: parseInt(e.target.value) || 0 })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Sede</label>
                    <select value={formData.sede_id || ''} onChange={e => setFormData({ ...formData, sede_id: e.target.value || null })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                      <option value="">Nessuna sede</option>
                      {sedi.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Responsabile Reparto</label>
                      <input value={formData.responsabile_reparto || ''} onChange={e => setFormData({ ...formData, responsabile_reparto: e.target.value })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="Dr. Nome Cognome" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Referente Tecnico</label>
                      <input value={formData.referente_tecnico || ''} onChange={e => setFormData({ ...formData, referente_tecnico: e.target.value })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="Tecnico Nome Cognome" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Email Reparto</label>
                      <input type="email" value={formData.email_reparto || ''} onChange={e => setFormData({ ...formData, email_reparto: e.target.value })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="reparto@ospedale.it" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Telefono</label>
                      <input value={formData.telefono_reparto || ''} onChange={e => setFormData({ ...formData, telefono_reparto: e.target.value })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="02-1234-567" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Nome Sede *</label>
                    <input value={formData.nome || ''} onChange={e => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                      placeholder="es. Laboratorio Principale" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Tipo Sede</label>
                    <select value={formData.tipo || 'laboratorio_principale'} onChange={e => setFormData({ ...formData, tipo: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                      {Object.entries(TIPO_SEDE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Indirizzo</label>
                      <input value={formData.indirizzo || ''} onChange={e => setFormData({ ...formData, indirizzo: e.target.value })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="Via Roma 1" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Città</label>
                      <input value={formData.citta || ''} onChange={e => setFormData({ ...formData, citta: e.target.value })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="Milano" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Responsabile</label>
                      <input value={formData.responsabile || ''} onChange={e => setFormData({ ...formData, responsabile: e.target.value })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="Dr. Nome Cognome" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Telefono</label>
                      <input value={formData.telefono || ''} onChange={e => setFormData({ ...formData, telefono: e.target.value })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                        placeholder="02-12345678" />
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                <textarea value={formData.note || ''} onChange={e => setFormData({ ...formData, note: e.target.value })} rows={3}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500 resize-none"
                  placeholder="Note aggiuntive..." />
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="is_active" checked={formData.is_active ?? true} onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-border text-sky-500" />
                <label htmlFor="is_active" className="text-sm font-semibold text-secondary-color">Attivo</label>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-border rounded-lg text-secondary-color hover:bg-surface-elevated font-semibold transition-colors">
                Annulla
              </button>
              <button onClick={handleSave} disabled={saving || !formData.nome}
                className="flex-1 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors">
                {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Crea'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Elimina elemento"
        message={`Sei sicuro di voler eliminare "${deleteConfirm.name}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
